import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { ForgeViewer } from '../../helpers/forge-viewer';
import AutodeskForgeViewer, { AutodeskForgeViewerProps } from '../AutodeskForgeViewer/AutodeskForgeViewer';
import { FirstPersonTool, FirstPersonToolEvents } from '@library/forge-viewer/helpers/plugins/first-person-tool';
import { FirstPersonViewerRef } from '@library/forge-viewer/helpers/forge-viewer.types';

interface FirstPersonViewerProps extends AutodeskForgeViewerProps {
    initialPosition: { x: number; y: number; z: number };
    initialDirection: { x: number; y: number; z: number };
    defaultPitch?: number;
    defaultYaw?: number;
    onPanChange?: (pos: { pitch: number; yaw: number }) => void;
    onZoomChange?: (zoom: number) => void;
}

const FirstPersonViewer = forwardRef<FirstPersonViewerRef, FirstPersonViewerProps>((props, ref) => {
    const {
        accessToken,
        urn,
        onObjectTreeCreated,
        onGeometryLoaded,
        onInteract,
        initialPosition,
        initialDirection,
        defaultPitch,
        defaultYaw,
        onPanChange,
        onZoomChange,
    } = props;

    const firstPersonToolRef = useRef<FirstPersonTool>(null);

    useImperativeHandle(ref, () => ({
        getPan: () => {
            return firstPersonToolRef.current?.getPan();
        },
        getZoom: () => {
            return firstPersonToolRef.current?.getZoom();
        },
        setPan: (pos: { pitch: number; yaw: number }) => {
            firstPersonToolRef.current?.setPan(pos);
        },
        setZoom: (zoom: number) => {
            firstPersonToolRef.current?.setZoom(zoom);
        },
        animateToPosition: (position: THREE.Vector3) => {
            firstPersonToolRef.current?.animateToPosition(position);
        },
        getForgeViewerInstance: () => {
            return firstPersonToolRef.current?.getForgeViewer() || null;
        },
    }));

    useEffect(() => {
        firstPersonToolRef.current = new FirstPersonTool({
            defaultPitch,
            defaultYaw,
        });
    }, [defaultPitch, defaultYaw]);

    useEffect(() => {
        if (!firstPersonToolRef.current) return;

        const panChangeFn = (pos: { pitch: number; yaw: number }) => {
            onPanChange?.(pos);
        };

        const zoomChangeFn = (zoom: number) => {
            onZoomChange?.(zoom);
        };

        firstPersonToolRef.current.on(FirstPersonToolEvents.PAN_CHANGE, panChangeFn);

        firstPersonToolRef.current.on(FirstPersonToolEvents.ZOOM_CHANGE, zoomChangeFn);

        return () => {
            firstPersonToolRef.current?.off(FirstPersonToolEvents.PAN_CHANGE, panChangeFn);

            firstPersonToolRef.current?.off(FirstPersonToolEvents.ZOOM_CHANGE, zoomChangeFn);
        };
    }, [onPanChange, onZoomChange]);

    const handleObjectTreeCreated = useCallback(
        (viewer: ForgeViewer, e: any) => {
            if (!firstPersonToolRef.current) return;

            firstPersonToolRef.current.attach(viewer);

            // Order matters: set direction first, then position
            viewer.camera.setDirection(new THREE.Vector3(initialDirection.x, initialDirection.y, initialDirection.z));
            viewer.camera.setPosition(new THREE.Vector3(initialPosition.x, initialPosition.y, initialPosition.z));

            firstPersonToolRef.current.setRotation({ pitch: 0, yaw: 0 });
            firstPersonToolRef.current.setFOV(90);

            onObjectTreeCreated?.(viewer, e);
        },
        [
            initialDirection.x,
            initialDirection.y,
            initialDirection.z,
            initialPosition.x,
            initialPosition.y,
            initialPosition.z,
            onObjectTreeCreated,
        ]
    );

    return (
        <AutodeskForgeViewer
            accessToken={accessToken}
            urn={urn}
            onObjectTreeCreated={handleObjectTreeCreated}
            onGeometryLoaded={onGeometryLoaded}
            onInteract={onInteract}
        />
    );
});

export default memo(FirstPersonViewer);
